import { useEffect } from 'react';
import { isTauri } from '../utils/tauri';
import { IS_COARSE_POINTER } from '../utils/pointer';

// URL바 개폐(~60px)·회전 과도기·소수점 오차와 키보드(가로모드 최소 ~150px)를 가르는 임계값.
// iPadOS 플로팅/분리 키보드는 vv.height를 줄이지 않으므로 자연히 대상 밖(오버레이 — 기존 동작 유지).
const KEYBOARD_MIN_PX = 100;

/**
 * 소프트 키보드가 layout viewport를 줄이지 못하는 브라우저(iOS Safari/PWA,
 * viewport meta의 interactive-widget을 모르는 구형 Android)에서 앱(#root)을
 * visualViewport 크기로 줄이고 패닝을 따라가게 해 채팅 헤더(뒤로가기)가
 * 화면 밖으로 밀리지 않게 한다. 키보드가 내려가면 원복.
 *
 * - iOS는 키보드가 뜨면 layout viewport는 그대로 두고 visual viewport를 아래로
 *   패닝해 포커스 입력을 드러낸다. 이 패닝은 프로그램으로 리셋할 수 없다 —
 *   문서 높이 ≤ layout viewport 높이면 스크롤 범위가 0이라 window.scrollTo는 no-op.
 *   그래서 싸우지 않고 translateY(vv.pageTop)로 추적한다(visual-viewport 고정 에뮬레이션).
 *   사용자가 페이지를 끌어 패닝해도 vv scroll 이벤트로 계속 따라붙는다(미세 jitter는
 *   이 기법의 알려진 한계).
 * - transform은 body가 아니라 #root에 건다. GlobalTooltip·ImageLightbox는
 *   "조상 transform이 position:fixed 기준을 깨므로 body로 포털 탈출"하는 구조라
 *   (각 파일 상단 주석 참조) body에 걸면 그 회피책이 무너진다. ContextMenu는 반대로
 *   body fixed가 iOS 키보드 패닝 중 좌표계가 어긋나는 사고를 겪고 #root 포털 +
 *   absolute로 옮겨 왔다 — 이 transform이 곧 그 배치의 기준이다. #root 안의 fixed
 *   요소(Modal·Toast 등)는 containing block이 보이는 영역으로 바뀌는데, 이는 회귀가
 *   아니라 개선이다(토스트가 키보드 위에 뜨고 모달이 보이는 영역에 정렬된다).
 * - interactive-widget=resizes-content가 적용된 Android Chrome 108+에서는 키보드가
 *   뜨면 layout viewport(ICB)가 함께 줄어 gap≈0이 되므로 이 훅은 저절로 no-op이다 —
 *   UA 스니핑 없이 "layout viewport가 이미 줄어든 브라우저는 건드리지 않는다"가 성립한다.
 * - 키보드가 떠 있는 동안 `html.kb-open` 클래스를 세운다 — index.css가 이 마커로
 *   하단 safe-area 인셋 패딩을 접는다(키보드가 홈 인디케이터를 이미 덮고 있어 무의미).
 */
export function useSoftKeyboardViewportFix(): void {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv || isTauri() || !IS_COARSE_POINTER) return; // 데스크톱·Tauri no-op
    const root = document.getElementById('root');
    if (!root) return;

    let applied = false;

    const release = () => {
      if (!applied) return;
      applied = false;
      root.style.removeProperty('height');
      root.style.removeProperty('transform');
      document.documentElement.classList.remove('kb-open');
    };

    const update = () => {
      // 핀치줌 중에는 개입 금지 — vv.height 감소가 키보드가 아니라 줌 배율 때문
      if (Math.abs((vv.scale ?? 1) - 1) > 0.01) return;
      // ⚠ 기준은 window.innerHeight가 아니라 documentElement.clientHeight(layout viewport 높이).
      // iOS Safari는 innerHeight가 visual viewport를 따라가므로 키보드가 뜨면 vv.height와
      // 함께 줄어들어 innerHeight 기준 gap은 항상 ≈0 — 훅이 영영 발동하지 않는다.
      // clientHeight는 iOS에서 키보드에 줄지 않고, resizes-content Android에서는 ICB와
      // 함께 줄어들어 "이미 줄어든 브라우저는 no-op" 규칙도 그대로 성립한다.
      const gap = document.documentElement.clientHeight - vv.height;
      if (gap > KEYBOARD_MIN_PX) {
        // 인라인 style이라 #root의 Tailwind h-full을 자연히 이긴다
        root.style.setProperty('height', `${Math.round(vv.height)}px`);
        root.style.setProperty('transform', `translateY(${Math.round(vv.pageTop)}px)`);
        document.documentElement.classList.add('kb-open');
        applied = true;
      } else {
        release();
      }
    };

    // resize: 키보드 개폐·회전 / scroll: 키보드가 뜬 동안 iOS가 visual viewport를 패닝할 때
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      release();
    };
  }, []);
}
